import React, { forwardRef, useId } from 'react'
import { IoCheckmarkCircle, IoAlertCircle } from 'react-icons/io5'

type InputVariant = 'default' | 'filled'
type InputSize = 'sm' | 'md' | 'lg'

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text displayed above the input */
  label?: string
  /** Error message - when present, input shows error state */
  error?: string
  /** Helper text displayed below the input */
  helperText?: string
  /** Show success state (green checkmark) */
  success?: boolean
  /** Success message */
  successMessage?: string
  /** Visual variant */
  variant?: InputVariant
  /** Size preset */
  size?: InputSize
  /** Left icon/addon */
  leftIcon?: React.ReactNode
  /** Right icon/addon */
  rightIcon?: React.ReactNode
  /** Full width input */
  fullWidth?: boolean
  /** Container class name */
  containerClassName?: string
}

/**
 * Accessible input component with validation states
 *
 * @example
 * // Basic input
 * <Input label="Email" type="email" placeholder="Enter your email" />
 *
 * @example
 * // With validation error
 * <Input
 *   label="Stake"
 *   type="number"
 *   error={errors.stake}
 *   helperText="Enter a value between 1 and 1000"
 * />
 *
 * @example
 * // With success state
 * <Input label="Username" value={username} success={isAvailable} successMessage="Username available" />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(
    {
      label,
      error,
      helperText,
      success,
      successMessage,
      variant = 'default',
      size = 'md',
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      required,
      className = '',
      containerClassName = '',
      id: providedId,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) {
    // Generate unique IDs for accessibility
    const generatedId = useId()
    const inputId = providedId || generatedId
    const errorId = `${inputId}-error`
    const helperId = `${inputId}-helper`

    // Determine which description IDs to use
    const descriptionIds: string[] = []
    if (error) descriptionIds.push(errorId)
    if (helperText && !error) descriptionIds.push(helperId)
    if (ariaDescribedBy) descriptionIds.push(ariaDescribedBy)

    const hasError = Boolean(error)
    const hasSuccess = success && !hasError

    // Build container class name
    const containerClasses = [
      'input-container',
      fullWidth && 'input-container-full',
      containerClassName,
    ]
      .filter(Boolean)
      .join(' ')

    // Build input class name
    const inputClasses = [
      'input',
      `input-${variant}`,
      `input-${size}`,
      hasError && 'input-error',
      hasSuccess && 'input-success',
      leftIcon && 'input-with-left-icon',
      (rightIcon || hasError || hasSuccess) && 'input-with-right-icon',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={inputId} className="input-label">
            {label}
            {required && <span className="input-required" aria-hidden="true">*</span>}
          </label>
        )}

        <div className="input-wrapper">
          {leftIcon && (
            <span className="input-icon input-icon-left" aria-hidden="true">
              {leftIcon}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            className={inputClasses}
            disabled={disabled}
            required={required}
            aria-invalid={hasError}
            aria-describedby={descriptionIds.length > 0 ? descriptionIds.join(' ') : undefined}
            {...props}
          />

          {/* Status icons */}
          {hasError && (
            <span className="input-icon input-icon-right input-icon-error" aria-hidden="true">
              <IoAlertCircle size={18} />
            </span>
          )}
          {hasSuccess && !rightIcon && (
            <span className="input-icon input-icon-right input-icon-success" aria-hidden="true">
              <IoCheckmarkCircle size={18} />
            </span>
          )}
          {rightIcon && !hasError && !hasSuccess && (
            <span className="input-icon input-icon-right" aria-hidden="true">
              {rightIcon}
            </span>
          )}
        </div>

        {/* Error message */}
        {error && (
          <span
            id={errorId}
            className="input-message input-message-error"
            role="alert"
          >
            {error}
          </span>
        )}

        {/* Success message */}
        {hasSuccess && successMessage && (
          <span
            className="input-message input-message-success"
            role="status"
          >
            {successMessage}
          </span>
        )}

        {/* Helper text (only show if no error) */}
        {helperText && !error && !successMessage && (
          <span id={helperId} className="input-message input-message-helper">
            {helperText}
          </span>
        )}
      </div>
    )
  }
)

// CSS for Input component (add to styles.css)
export const inputStyles = `
/* ============================================
   INPUT COMPONENT
   ============================================ */

.input-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-container-full {
  width: 100%;
}

.input-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text);
}

.input-required {
  color: var(--red);
  margin-left: 2px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input {
  width: 100%;
  padding: 12px 14px;
  font-size: 16px; /* Prevents iOS zoom */
  font-family: inherit;
  color: var(--input-text, var(--text));
  background: var(--input-bg, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--input-border, var(--border));
  border-radius: 10px;
  transition: all 0.15s ease;
  -webkit-appearance: none;
  appearance: none;
}

.input::placeholder {
  color: var(--muted);
}

.input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-glow);
}

.input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Sizes */
.input-sm {
  padding: 8px 12px;
  font-size: 14px;
}

.input-lg {
  padding: 14px 16px;
  font-size: 18px;
}

/* Variants */
.input-filled {
  background: var(--card);
  border-color: transparent;
}

.input-filled:focus {
  border-color: var(--accent);
}

/* States */
.input-error {
  border-color: var(--red) !important;
}

.input-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
}

.input-success {
  border-color: var(--green) !important;
}

.input-success:focus {
  box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.2);
}

/* Icons */
.input-icon {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  pointer-events: none;
}

.input-icon-left {
  left: 12px;
}

.input-icon-right {
  right: 12px;
}

.input-icon-error {
  color: var(--red);
}

.input-icon-success {
  color: var(--green);
}

.input-with-left-icon {
  padding-left: 40px;
}

.input-with-right-icon {
  padding-right: 40px;
}

/* Messages */
.input-message {
  font-size: 12px;
  line-height: 1.4;
}

.input-message-helper {
  color: var(--muted);
}

.input-message-error {
  color: var(--red);
}

.input-message-success {
  color: var(--green);
}

/* Number input - hide spinners */
.input[type="number"]::-webkit-inner-spin-button,
.input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.input[type="number"] {
  -moz-appearance: textfield;
}
`

export default Input
